/**
 * Clerk Auth Repository Implementation
 * Concrete implementation of IAuthRepository using Clerk Auth
 */

import type { User, EmailLinkAuthDTO } from '@/dto';
import type { IAuthRepository } from '../interfaces/IAuthRepository';

// Clerk user type (fields we actually use)
interface ClerkUser {
  id: string;
  primaryEmailAddress?: { emailAddress: string } | null;
  fullName?: string | null;
  imageUrl?: string | null;
  emailAddresses: Array<{ emailAddress: string }>;
}

/**
 * Bridge class to map Clerk user to Firebase User interface
 * This allows us to keep the existing IAuthRepository interface
 * while migrating to Clerk
 */
class ClerkUserBridge implements Partial<User> {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;

  constructor(clerkUser: ClerkUser) {
    this.uid = clerkUser.id;
    this.email = clerkUser.primaryEmailAddress?.emailAddress ?? null;
    this.displayName = clerkUser.fullName ?? null;
    this.photoURL = clerkUser.imageUrl ?? null;
  }

  // Required by Firebase User interface but not used in our app
  get providerId(): string {
    return 'clerk';
  }

  // Stub methods to satisfy Firebase User interface
  async getIdToken(): Promise<string> {
    throw new Error('Use repository.getIdToken() instead');
  }

  async getIdTokenResult(): Promise<any> {
    throw new Error('Use repository.getIdToken() instead');
  }
}

/**
 * Clerk Auth Repository
 *
 * Note: This repository works alongside ClerkProvider in the React tree.
 * The actual authentication state is managed by Clerk's useAuth() hook.
 * This repository provides an adapter layer to maintain compatibility
 * with the existing IAuthRepository interface.
 */
export class ClerkAuthRepository implements IAuthRepository {
  private clerkInstance: any = null;
  private currentClerkUser: ClerkUser | null = null;
  private authStateCallbacks: Array<(user: User | null) => void> = [];

  /**
   * Initialize repository with Clerk instance
   * This should be called from AuthContext after ClerkProvider is mounted
   */
  setClerkInstance(clerk: any) {
    this.clerkInstance = clerk;
  }

  /**
   * Update current user and notify listeners
   * This should be called from AuthContext when Clerk's user state changes
   */
  setCurrentUser(clerkUser: ClerkUser | null) {
    this.currentClerkUser = clerkUser;
    const bridgedUser = clerkUser ? new ClerkUserBridge(clerkUser) as User : null;
    this.authStateCallbacks.forEach((callback) => callback(bridgedUser));
  }

  private ensureClerk() {
    if (!this.clerkInstance) {
      throw new Error('Clerk is not initialized. Make sure ClerkProvider is mounted before using auth.');
    }
    return this.clerkInstance;
  }

  async signIn(email: string, password: string): Promise<User> {
    const clerk = this.ensureClerk();

    try {
      const result = await clerk.client.signIn.create({
        identifier: email,
        password,
      });

      if (result.status === 'complete') {
        await clerk.setActive({ session: result.createdSessionId });
        const clerkUser = clerk.user;
        if (!clerkUser) {
          throw new Error('Sign in successful but user not found');
        }
        return new ClerkUserBridge(clerkUser) as User;
      } else {
        throw new Error('Sign in requires additional steps');
      }
    } catch (error: any) {
      console.error('[ClerkAuthRepository] Sign in error:', error);
      throw new Error(error.errors?.[0]?.message || 'Failed to sign in');
    }
  }

  async signUp(email: string, password: string): Promise<User> {
    const clerk = this.ensureClerk();

    try {
      const result = await clerk.client.signUp.create({
        emailAddress: email,
        password,
      });

      // Clerk may require email verification
      if (result.status === 'complete') {
        await clerk.setActive({ session: result.createdSessionId });
        const clerkUser = clerk.user;
        if (!clerkUser) {
          throw new Error('Sign up successful but user not found');
        }
        return new ClerkUserBridge(clerkUser) as User;
      } else {
        throw new Error('Sign up requires email verification');
      }
    } catch (error: any) {
      console.error('[ClerkAuthRepository] Sign up error:', error);
      throw new Error(error.errors?.[0]?.message || 'Failed to sign up');
    }
  }

  async sendSignInLinkToEmail(params: EmailLinkAuthDTO): Promise<void> {
    const clerk = this.ensureClerk();

    try {
      // Clerk's magic link implementation
      const result = await clerk.client.signIn.create({
        identifier: params.email,
        strategy: 'email_link',
        redirectUrl: params.continueUrl,
      });

      if (result.status !== 'complete') {
        // Store email for verification flow
        window.localStorage.setItem('emailForSignIn', params.email);
      }
    } catch (error: any) {
      console.error('[ClerkAuthRepository] Send magic link error:', error);
      throw new Error(error.errors?.[0]?.message || 'Failed to send magic link');
    }
  }

  async signInWithEmailLink(_email: string, emailLink: string): Promise<User> {
    const clerk = this.ensureClerk();

    try {
      // Extract the token from the email link
      const url = new URL(emailLink);
      const token = url.searchParams.get('__clerk_ticket');

      if (!token) {
        throw new Error('Invalid email link - missing verification token');
      }

      const result = await clerk.client.signIn.create({
        strategy: 'email_link',
        emailAddressId: token,
      });

      if (result.status === 'complete') {
        await clerk.setActive({ session: result.createdSessionId });
        const clerkUser = clerk.user;
        if (!clerkUser) {
          throw new Error('Email link sign in successful but user not found');
        }

        // Clear stored email
        window.localStorage.removeItem('emailForSignIn');

        return new ClerkUserBridge(clerkUser) as User;
      } else {
        throw new Error('Email link sign in requires additional steps');
      }
    } catch (error: any) {
      console.error('[ClerkAuthRepository] Email link sign in error:', error);
      throw new Error(error.errors?.[0]?.message || 'Failed to sign in with email link');
    }
  }

  isSignInWithEmailLink(emailLink: string): boolean {
    try {
      const url = new URL(emailLink);
      return url.searchParams.has('__clerk_ticket');
    } catch {
      return false;
    }
  }

  async signOut(): Promise<void> {
    const clerk = this.ensureClerk();
    await clerk.signOut();
  }

  getCurrentUser(): User | null {
    if (!this.currentClerkUser) return null;
    return new ClerkUserBridge(this.currentClerkUser) as User;
  }

  onAuthStateChanged(callback: (user: User | null) => void): () => void {
    this.authStateCallbacks.push(callback);

    // Immediately call with current user
    const currentUser = this.getCurrentUser();
    callback(currentUser);

    // Return unsubscribe function
    return () => {
      const index = this.authStateCallbacks.indexOf(callback);
      if (index > -1) {
        this.authStateCallbacks.splice(index, 1);
      }
    };
  }

  async getIdToken(_forceRefresh = false): Promise<string | null> {
    const clerk = this.ensureClerk();

    if (!clerk.session) return null;

    try {
      // Clerk's getToken method
      const token = await clerk.session.getToken();
      return token;
    } catch (error) {
      console.error('[ClerkAuthRepository] Get token error:', error);
      return null;
    }
  }

  async sendPasswordResetEmail(email: string): Promise<void> {
    const clerk = this.ensureClerk();

    try {
      await clerk.client.signIn.create({
        identifier: email,
        strategy: 'reset_password_email_code',
      });
    } catch (error: any) {
      console.error('[ClerkAuthRepository] Send password reset error:', error);
      throw new Error(error.errors?.[0]?.message || 'Failed to send password reset email');
    }
  }

  async confirmPasswordReset(code: string, newPassword: string): Promise<void> {
    const clerk = this.ensureClerk();

    try {
      const result = await clerk.client.signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code,
        password: newPassword,
      });

      if (result.status === 'complete') {
        await clerk.setActive({ session: result.createdSessionId });
      } else {
        throw new Error('Password reset requires additional steps');
      }
    } catch (error: any) {
      console.error('[ClerkAuthRepository] Confirm password reset error:', error);
      throw new Error(error.errors?.[0]?.message || 'Failed to reset password');
    }
  }

  async updatePassword(newPassword: string): Promise<void> {
    const clerk = this.ensureClerk();

    if (!clerk.user) {
      throw new Error('No authenticated user');
    }

    try {
      await clerk.user.updatePassword({ newPassword });
    } catch (error: any) {
      console.error('[ClerkAuthRepository] Update password error:', error);
      throw new Error(error.errors?.[0]?.message || 'Failed to update password');
    }
  }
}
